import { FormattedMessage } from 'react-intl';

import { CustomDateTime } from '@/data/formatting/CustomDateTime';
import { EngineOilPreconditions } from '@/generated/fleetAggregationServiceTypes';

type EngineOilDidDynamicPreconditionsElementProps = {
    engineOilPreconditions: EngineOilPreconditions;
};

const PreconditionItem = ({ isValid, messageId, values, disabled, index }: { isValid?: boolean; messageId: string; values?: any; disabled?: boolean; index: number }) => {
    let preconditionIcon = '';
    let preconditionItemColor = '';

    if (isValid === undefined || disabled) {
        // any precondition should already come from the backend as valid=undefined when it should be disabled
        // meaning it should already be undefined when the external tester is connected, but we double-check
        preconditionIcon = 'rioglyph-minus-sign text-color-dark';
        preconditionItemColor = 'text-color-dark';
    } else if (isValid) {
        preconditionIcon = 'rioglyph-ok-sign text-color-success';
    } else {
        preconditionIcon = 'rioglyph-remove-sign text-color-danger';
    }

    return (
        <div className={`padding-y-10 ${preconditionItemColor}`}>
            <span className={`icon rioglyph margin-right-5 text-size-20 ${preconditionIcon}`} />
            <span>{index}. </span>
            <FormattedMessage id={messageId} values={values} />
        </div>
    );
};

export { PreconditionItem };

const EngineOilDidDynamicPreconditionsElement = ({ engineOilPreconditions }: EngineOilDidDynamicPreconditionsElementProps) => {
    const measuredAtDatetime = engineOilPreconditions.measured_at.date ? new Date(engineOilPreconditions.measured_at.date) : undefined;
    const isMoreThan24HoursAgo =
        measuredAtDatetime instanceof Date && !isNaN(measuredAtDatetime.getTime()) ? Date.now() - measuredAtDatetime.getTime() > 24 * 60 * 60 * 1000 : false;

    return (
        <div>
            <div className="text-bold">
                <FormattedMessage
                    id="intl-msg:fleetstatus.diagnostics.sidebar.section.engine_oil.state.preconditions.dynamic.dialog.statusAt"
                    values={{
                        dt: <CustomDateTime value={measuredAtDatetime} showWeekDay={false} />,
                    }}
                />
            </div>
            <div className="margin-top-25">
                <PreconditionItem
                    isValid={engineOilPreconditions.is_external_tester_disconnected}
                    messageId="intl-msg:fleetstatus.diagnostics.sidebar.section.engine_oil.state.preconditions.dynamic.dialog.externalConnectors"
                    disabled={false}
                    index={1}
                />
                <PreconditionItem
                    isValid={engineOilPreconditions.is_standing_on_flat_surface}
                    messageId="intl-msg:fleetstatus.diagnostics.sidebar.section.engine_oil.state.preconditions.dynamic.dialog.flatSurface"
                    disabled={!engineOilPreconditions.is_external_tester_disconnected}
                    index={2}
                />
                <PreconditionItem
                    isValid={engineOilPreconditions.is_standing_time_valid}
                    messageId="intl-msg:fleetstatus.diagnostics.sidebar.section.engine_oil.state.preconditions.dynamic.dialog.standingTime"
                    values={{
                        b: (innerText: any) => <b>{innerText}</b>,
                    }}
                    disabled={!engineOilPreconditions.is_external_tester_disconnected}
                    index={3}
                />
                <PreconditionItem
                    isValid={engineOilPreconditions.is_ignition_on_for_six_seconds}
                    messageId="intl-msg:fleetstatus.diagnostics.sidebar.section.engine_oil.state.preconditions.dynamic.dialog.ignition"
                    values={{
                        b: (innerText: any) => <b>{innerText}</b>,
                    }}
                    disabled={!engineOilPreconditions.is_external_tester_disconnected}
                    index={4}
                />
                <PreconditionItem
                    isValid={engineOilPreconditions.is_engine_not_running}
                    messageId="intl-msg:fleetstatus.diagnostics.sidebar.section.engine_oil.state.preconditions.dynamic.dialog.engine"
                    values={{
                        b: (innerText: any) => <b>{innerText}</b>,
                    }}
                    disabled={!engineOilPreconditions.is_external_tester_disconnected}
                    index={5}
                />
                <PreconditionItem
                    isValid={engineOilPreconditions.is_engine_oil_level_valid && !isMoreThan24HoursAgo}
                    messageId="intl-msg:fleetstatus.diagnostics.sidebar.section.engine_oil.state.preconditions.dynamic.dialog.signal"
                    disabled={!engineOilPreconditions.is_external_tester_disconnected}
                    index={6}
                />
            </div>
            <div className="padding-top-25">
                <FormattedMessage id="intl-msg:fleetstatus.diagnostics.sidebar.section.engine_oil.state.preconditions.dynamic.dialog.moreDetails" />
            </div>
            <div className="text-bold padding-top-10">
                <FormattedMessage id="intl-msg:fleetstatus.diagnostics.sidebar.section.engine_oil.state.preconditions.dynamic.dialog.fulfillAllForValid" />
            </div>
        </div>
    );
};

export default EngineOilDidDynamicPreconditionsElement;
